import { Action, createAction, handleActions } from 'redux-actions'
import { call, put, select, takeLatest, takeLeading } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import * as Bridge from '@/api/BridgeApi'
import {
  DEBIT_CARD_API_URL,
  fetchDebitCardInfo,
  IDebitCardShowInfoResponse,
} from '@/api/DebitCardApi'
import { CUSTOMER_API_URL } from '@/api/ProfileApi'
import { LEAVE_ALERT_TYPE } from '@/components/common/layer/Leave/LeaveLayer'
import { NOTICE_ALERT_TYPE } from '@/components/common/layer/Notice/NoticeLayer'
import { DEBIT_CARD_ROUTES, ENTRY_ROUTE_QUERY, STATUS_CODE } from '@/domains/CommonDomain'
import { LOG_EVENT_NAME } from '@/domains/CommonDomain'
import { IDebitCardInfo } from '@/domains/DebitCardDomain'
import { IRootState } from '@/modules'
import { errorActions } from '@/modules/common/error'
import { layerActions } from '@/modules/common/layer'
import { createFetchAction, createPostAction, createSaga } from '@/utils/actionUtils'
import {
  HostType,
  IRequestPinAuthResult,
  RequestPinAuthAction,
} from '@/utils/bridgeUtils/features/requestPinAuth'
import { ISetDebitCardPincodeResult } from '@/utils/bridgeUtils/features/setDebitCardPincode'
import { newError } from '@/utils/errorUtils'
import { checkEntry, checkReset, goBack, redirect } from '@/utils/historyUtils'
import { apiFailureReducer, apiSuccessReducer, reset } from '@/utils/reducerUtils'
import af from '@/utils/trackingUtils'
import {
  isValidCvv,
  isValidDebitCardNo,
  isValidDebitCardPinNo,
  isValidExpiryDateString,
} from '@/utils/validationUtils'

export interface IDebitCardActivateView {
  isInvalidByServer: boolean
  cvv: string
  debitCardNo: string
  debitCardPinNo: string
  expiryDateString: string
}

interface IDebitCardActivateState extends IDebitCardShowInfoResponse {
  view: IDebitCardActivateView
}

const defaultView: IDebitCardActivateView = {
  isInvalidByServer: false,
  cvv: '',
  debitCardNo: '',
  debitCardPinNo: '',
  expiryDateString: '',
}

const initialState: IDebitCardActivateState = {
  debitCardInfoList: [],
  selectedCardIndex: 0,
  view: defaultView,
}

export const DEBIT_CARD_ACTIVATE = 'DebitCardActivate'

export const GET_DEBIT_CARD_INFO_WITH_BENEFIT = `${DEBIT_CARD_ACTIVATE}/getDebitCardInfoWithBenefit`
export const SET_VIEW = `${DEBIT_CARD_ACTIVATE}/setView`
export const SET_PIN_CODE = `${DEBIT_CARD_ACTIVATE}/setDebitCardPincode`
export const ACTIVATE_DEBIT_CARD = `${DEBIT_CARD_ACTIVATE}/activate`
export const CLEAR_STATE = `${DEBIT_CARD_ACTIVATE}/clearState`

export const debitCardActivateActions = {
  setView: createAction(SET_VIEW),
  setDebitCardPincode: createAction(SET_PIN_CODE),
  clearState: createAction(CLEAR_STATE),
}

export const debitCardInfoWithBenefitAsync = createFetchAction(GET_DEBIT_CARD_INFO_WITH_BENEFIT)
export const debitCardActivateAsync = createPostAction(ACTIVATE_DEBIT_CARD)

export const reducer = {
  [debitCardActivateAsync.SUCCESS]: apiSuccessReducer,
  [debitCardActivateAsync.FAILURE]: apiFailureReducer,
  [debitCardInfoWithBenefitAsync.SUCCESS]: apiSuccessReducer,
  [debitCardInfoWithBenefitAsync.FAILURE]: apiFailureReducer,
  [SET_VIEW]: (state: IDebitCardActivateState, action: Action<any>) => ({
    ...state,
    view: {
      ...state.view,
      ...action.payload,
    },
  }),
  [CLEAR_STATE]: (state: IDebitCardActivateState) => reset(state, initialState),
}

export const debitCardActivateSelector = {
  debitCardInfo: (state: IRootState): IDebitCardInfo => {
    const selectedCardIndex = oc(state[DEBIT_CARD_ACTIVATE]).selectedCardIndex(0)
    const debitCardInfoList = oc(state[DEBIT_CARD_ACTIVATE]).debitCardInfoList([])

    return debitCardInfoList[selectedCardIndex] || {}
  },
  view: (state: IRootState) => oc(state[DEBIT_CARD_ACTIVATE]).view(defaultView),
  isValid: (state: IRootState): boolean => {
    const view = debitCardActivateSelector.view(state)
    const { isInvalidByServer, cvv, debitCardNo, debitCardPinNo, expiryDateString } = view

    return (
      !isInvalidByServer &&
      isValidCvv(cvv) &&
      isValidDebitCardNo(debitCardNo) &&
      isValidDebitCardPinNo(debitCardPinNo) &&
      isValidExpiryDateString(expiryDateString)
    )
  },
}

export function* setDebitCardPincode() {
  try {
    const { result }: ISetDebitCardPincodeResult = yield call(Bridge.setDebitCardPin)

    if (!result) {
      throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
    }
    if (!result.isSuccess) {
      const { code, action } = JSON.parse(result.response)

      if (action === RequestPinAuthAction.CLOSE) {
        return
      }

      throw newError(code)
    }
    if (result.confirmPin) {
      yield put(debitCardActivateActions.setView({ debitCardPinNo: result.confirmPin }))
    }
  } catch (e) {
    yield put(errorActions.set(e))
  }
}

export function* activateDebitCard(action: Action<string>) {
  const acctNo = action.payload

  try {
    af.send(LOG_EVENT_NAME.APP_DEBITCARD_ACTIVATION_START)
    const isReset = yield call(checkReset)
    const { isInvalidByServer, ...debitCardInfo }: IDebitCardActivateView = yield select(
      debitCardActivateSelector.view,
    )
    const hostType = isReset ? HostType.CUSTOMER_API : HostType.BANK_API
    const apiUrl = isReset ? CUSTOMER_API_URL.DEBIT_CARD_PINCODE_RESET : DEBIT_CARD_API_URL.ACTIVATE
    const request = {
      ...debitCardInfo,
      expiryDateString: debitCardInfo.expiryDateString.replace(/\//g, ''),
    }
    const { result }: IRequestPinAuthResult = yield call(Bridge.enterTransactionPinForDebitCard, {
      hostType,
      apiUrl,
      request,
    })

    if (!result) {
      throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
    }
    if (result.action === RequestPinAuthAction.CLOSE) {
      return
    }
    if (
      result.action === RequestPinAuthAction.AUTH_FAIL ||
      result.action === RequestPinAuthAction.AUTH_ERROR
    ) {
      // const { code } = JSON.parse(result.response)
      // throw newError(code)
      throw JSON.parse(result.response)
    }

    if (result.action === RequestPinAuthAction.AUTH_SUCCESS) {
      const isEntry = yield call(checkEntry)

      if (isEntry) {
        const { debitCardNo } = debitCardInfo

        yield call(
          redirect,
          `${DEBIT_CARD_ROUTES.DETAIL}/${debitCardNo}/${acctNo}?${ENTRY_ROUTE_QUERY}`,
        )
        af.send(LOG_EVENT_NAME.APP_DEBITCARD_ACTIVATION_SUCCESS)

        yield put(layerActions.open(NOTICE_ALERT_TYPE.ACTIVATION))
      } else {
        yield call(goBack)
        const noticeAlertType = isReset
          ? NOTICE_ALERT_TYPE.RESET_DEBIT_CARD_PIN
          : NOTICE_ALERT_TYPE.ACTIVATION
        yield put(layerActions.open(noticeAlertType))
      }
    }
  } catch (e) {
    switch (e.code) {
      case STATUS_CODE.INVALID_CARD_INFO:
        yield put(
          debitCardActivateActions.setView({
            isInvalidByServer: true,
            cvv: '',
            debitCardNo: '',
            expiryDateString: '',
          }),
        )
        yield put(layerActions.open(LEAVE_ALERT_TYPE.INVALID_DEBIT_CARD_INFO))

        return
      default:
        yield put(errorActions.set(e))

        return
    }
  }
}

export const debitCardActivateReducer = handleActions(reducer, initialState)
export const debitCardActivateSaga = [
  takeLatest(
    debitCardInfoWithBenefitAsync.FETCH,
    createSaga(debitCardInfoWithBenefitAsync, fetchDebitCardInfo),
  ),
  takeLeading(debitCardActivateAsync.POST, activateDebitCard),
  takeLeading(SET_PIN_CODE, setDebitCardPincode),
]
